import { ParamSchemaType } from "@/lib/form/schema"
import { FormParamStatus } from "@/lib/form/types"
import {
  buildSchemaForRmProduct,
  Schema,
  buildSchemaForPmProduct,
} from "@/rmpm/lib/schema"
import {
  createRMPMProductSheet,
  GetProductsRes,
  PMProductSchema,
  RMPMParameterSchema,
  RMProductSchema,
} from "@/services/rmpm"
import { SuggestedEmployee } from "@/services/ipqc"
import { createUuid } from "@/lib/utils"
import { StateCreator } from "@/store/utils"
import { cloneDeep, get as lodashGet } from "lodash"

export type TruckType = "rm" | "pm"

// Internal truck parameter type (for storage/display - has all fields)
export interface TruckParameter {
  id: string
  name: string
  parameterId?: string
  value?: string | number | boolean
  remark?: string
  recordedAt?: string
  status?: FormParamStatus
  inputType?: ParamSchemaType
}

export interface Truck {
  truckNumber: string;
  sheetId: string,
  products: Schema[],
  truckParameters: TruckParameter[]
}

export interface HandoverTrucks {
  rm: Truck[]
  pm: Truck[]
}

export interface Handover {
  handoverFromEmployeeCode: string,
  handoverFromEmployeeSessionId: string,
  trucks: HandoverTrucks
}

export interface RMPMState {
  process: {
    processId: string
    shiftId: string
  }
  trucks: {
    rm: Schema[]
    pm: Schema[]
  }
  forms: {
    rm: Record<string, any>[]
    pm: Record<string, any>[]
  }
  allProducts: GetProductsRes
  submitted: Record<string, FormParamStatus>
  employees: SuggestedEmployee[]
  handovers: Handover[]
  resamples: Handover[]
}

export interface RMTruck {
  id: string
  truckNumber: string
  sheetId?: string  // Server-generated sheet ID
  products: string[]
}

export interface PMTruck {
  id: string
  truckNumber: string
  sheetId?: string  // Server-generated sheet ID
  pmType: string
  products: string[]
}



export function initialState(): RMPMState {
  return {
    process: {
      processId: "",
      shiftId: "",
    },
    trucks: {
      rm: [],
      pm: [],
    },
    forms: {
      rm: [],
      pm: [],
    },
    allProducts: { rmProducts: [], pmProducts: [], rmParameters: [], pmParameters: [] },
    submitted: {},
    employees: [],
    handovers: [],
    resamples: [],
  }
}

export interface RMPMSlice {
  rmpm: RMPMState & {
    setProcessDetails(details: RMPMState["process"]): void
    clearProcess(): void
    clearFormData(): void
    setAllProducts(products: GetProductsRes): void
    setEmployees(employeesList: SuggestedEmployee[]): void
    setHandover(handoverList: Handover[]): void
    setResample(resampleList: Handover[]): void
    getPmProductApiSchema(productId: string): PMProductSchema | null
    getRmProductApiSchema(productId: string): RMProductSchema | null
    getTruckMetaSchemaById(truckType: string, sheetId: string): Schema | null
    getTruckMetaSchemaByOnlyId(sheetId: string): Schema | null
    getTruckFormById(truckType: string, sheetId: string): Schema | null
    getPmParametersApiSchema(): RMPMParameterSchema[] | null
    getRmParametersApiSchema(): RMPMParameterSchema[] | null
    addRMPMTruck(truckType: string, truck: Schema): void
    addRMPMResampleTruck(truckType: string, truck: Schema): void
    addPmTrucksMetaSchemas(trucks: Schema[]): void
    addRmTrucksMetaSchemas(trucks: Schema[]): void
    setRmForm(values: Record<string, any>): void
    setPmForm(values: Record<string, any>): void
    addToSubmitted(sheetId: string): void
    removeToSubmitted(sheetId: string): void
    addProductsToRmTruck(sheetId: string, schemaIds: string[], productSheetIds?: Record<string, string>): void
    removeRmProduct(sheetId: string, productId: string): void
    addProductsToPmTruck(sheetId: string, schemaIds: string[], productSheetIds?: Record<string, string>): void
    removePmProduct(sheetId: string, productId: string): void
    addSkuToProduct(
      truckType: TruckType,
      sheetId: string,
      addToPath: string,
      schema: Schema,
      productInfo: { productId: string; productName: string; skuName: string; market?: "DOMESTIC" | "EXPORT" }
    ): Promise<void>
    removeSkuFromProduct(
      truckType: TruckType,
      sheetId: string,
      skuPath: string,
      itemId: string
    ): void
  }
}

function actionName(action: string): string {
  return `rmpm/${action}`
}


const createRMPMSlice: StateCreator<RMPMSlice> = (set, get) => ({
  rmpm: {
    ...initialState(),
    setProcessDetails(details) {
      set(
        (state: { rmpm: { process: { processId: string; shiftId: string } } }) => {
          state.rmpm.process = details

          return state
        },
        undefined,
        actionName("setProcessDetails")
      )
    },
    clearProcess() {
      set(
        (state: { session: { session: { processes: any[] } }; rmpm: { process: { processId: any }; allProducts: any } }) => {
          state.session.session.processes =
            state.session.session.processes.filter(
              (proc) => proc.processId !== state.rmpm.process.processId
            )

          state.rmpm = {
            ...state.rmpm,
            ...initialState(),
            allProducts: state.rmpm.allProducts,
          }

          return state
        },
        undefined,
        actionName("clearProcess")
      )
    },
    clearFormData() {
      set(
        (state: { rmpm: RMPMState }) => {
          state.rmpm.trucks = { rm: [], pm: [] }
          state.rmpm.forms = { rm: [], pm: [] }
          state.rmpm.submitted = {}
          state.rmpm.handovers = []
          state.rmpm.resamples = []

          return state
        },
        undefined,
        actionName("clearFormData")
      )
    },
    setAllProducts(products) {
      set(
        (state: { rmpm: { allProducts: GetProductsRes } }) => {
          state.rmpm.allProducts = products

          return state
        },
        undefined,
        actionName("setAllProducts")
      )
    },
    setEmployees(employeesList: SuggestedEmployee[]) {
      set(
        (state: { rmpm: { employees: SuggestedEmployee[] } }) => {
          state.rmpm.employees = employeesList

          return state
        },
        undefined,
        actionName("setEmployees")
      )
    },
    setHandover(handoverList: Handover[]) {
      set(
        (state: { rmpm: { handovers: Handover[] } }) => {
          state.rmpm.handovers = handoverList;
          return state;
        },
        undefined,
        actionName("setHandover")
      );
    },
    setResample(resampleList: Handover[]) {
      set(
        (state: { rmpm: { resamples: Handover[] } }) => {
          state.rmpm.resamples = resampleList;
          return state;
        },
        undefined,
        actionName("setResample")
      );
    },
    getPmProductApiSchema(productId) {
      const state = get().rmpm
      const schema = state.allProducts.pmProducts.find(
        (prod: { id: string }) => prod.id === productId
      )

      return schema || null
    },
    getRmProductApiSchema(productId) {
      const state = get().rmpm
      const schema = state.allProducts.rmProducts.find(
        (prod: { id: string }) => prod.id === productId
      )

      return schema || null
    },
    getTruckMetaSchemaById(truckType: "rm" | "pm", sheetId: string) {
      const state = get().rmpm
      const schema = state.trucks[truckType].find(
        (prod) => prod.sheetId !== undefined && prod.sheetId === sheetId
      )

      return schema || null
    },
    getTruckMetaSchemaByOnlyId(sheetId: string) {
      const state = get().rmpm;

      for (const type of Object.keys(state.trucks)) {
        const schema = state.trucks[type as TruckType]?.find(
          (truck) => truck.sheetId === sheetId
        );
        if (schema) return schema;
      }

      return null;
    },

    getTruckFormById(truckType: "rm" | "pm", sheetId: string) {
      const state = get().rmpm
      const schema = state.forms[truckType].find(
        (prod) => prod.sheetId !== undefined && prod.sheetId === sheetId
      )

      return schema || null
    },
    getPmParametersApiSchema() {
      const state = get().rmpm
      const schema = state.allProducts.pmParameters

      return schema || null
    },
    getRmParametersApiSchema() {
      const state = get().rmpm
      const schema = state.allProducts.rmParameters

      return schema || null
    },
    addPmTrucksMetaSchemas(trucks) {
      set(
        (state: { rmpm: { trucks: { pm: Schema[] } } }) => {
          for (const truck of trucks) {
            if (!truck?.sheetId) continue

            const foundIdx = state.rmpm.trucks.pm.findIndex(
              (item) => item.sheetId === truck.sheetId
            )

            if (foundIdx !== -1) {
              state.rmpm.trucks.pm[foundIdx] = truck
            } else {
              state.rmpm.trucks.pm.push(truck)
            }
          }
          return state
        },
        undefined,
        actionName("addPmTrucksMetaSchemas")
      )
    },
    addRmTrucksMetaSchemas(trucks) {
      set(
        (state: { rmpm: { trucks: { rm: Schema[] } } }) => {
          for (const truck of trucks) {
            if (!truck?.sheetId) continue

            const foundIdx = state.rmpm.trucks.rm.findIndex(
              (item) => item.sheetId === truck.sheetId
            )

            if (foundIdx !== -1) {
              state.rmpm.trucks.rm[foundIdx] = truck
            } else {
              state.rmpm.trucks.rm.push(truck)
            }
          }
          return state
        },
        undefined,
        actionName("addRmTrucksMetaSchemas")
      )
    },
    addRMPMTruck(truckType: string, truckSchema: Schema) {
      set(
        (state: { rmpm: { trucks: { [x: string]: any } } }) => {
          if (!truckSchema?.sheetId) return state;

          const trucks = state.rmpm.trucks[truckType];

          // find index
          const index = trucks.findIndex((item: { sheetId: string | undefined }) => item.sheetId === truckSchema.sheetId);

          if (index !== -1) {
            // update existing truck
            trucks[index] = { isHandover: true, ...truckSchema };
          } else {
            // insert new truck
            trucks.push({ isHandover: true, ...truckSchema });
          }

          return state;
        },
        undefined,
        actionName("addRMPMTruck")
      );
    },
    addRMPMResampleTruck(truckType: string, truckSchema: Schema) {
      set(
        (state: { rmpm: { trucks: { [x: string]: any } } }) => {
          if (!truckSchema?.sheetId) return state;

          const trucks = state.rmpm.trucks[truckType];

          // find index
          const index = trucks.findIndex((item: { sheetId: string | undefined }) => item.sheetId === truckSchema.sheetId);

          if (index !== -1) {
            // update existing truck
            trucks[index] = { isResample: true, ...truckSchema };
          } else {
            // insert new truck
            trucks.push({ isResample: true, ...truckSchema });
          }

          return state;
        },
        undefined,
        actionName("addRMPMResampleTruck")
      );
    },
    setRmForm(data: Schema) {
      set(
        (state: { rmpm: { forms: { rm: Schema[] } } }) => {
          if (!data?.sheetId) {
            return
          }

          const foundIdx = state.rmpm.forms.rm.findIndex(
            (item) => item.sheetId === data.sheetId
          )

          if (foundIdx !== -1) {
            state.rmpm.forms.rm[foundIdx] = data
          } else {
            state.rmpm.forms.rm.push(data)
          }

          return state
        },
        undefined,
        actionName("setRmForm")
      )
    },
    setPmForm(data: Schema) {
      set(
        (state: { rmpm: { forms: { pm: Schema[] } } }) => {
          if (!data?.sheetId) {
            return
          }

          const foundIdx = state.rmpm.forms.pm.findIndex(
            (item) => item.sheetId === data.sheetId
          )

          if (foundIdx !== -1) {
            state.rmpm.forms.pm[foundIdx] = data
          } else {
            state.rmpm.forms.pm.push(data)
          }

          return state
        },
        undefined,
        actionName("setPmForm")
      )
    },
    addToSubmitted(id) {
      set(
        (state: { rmpm: { submitted: { [x: string]: string } } }) => {
          state.rmpm.submitted[id] = FormParamStatus.SUBMITTED
          return state
        },
        undefined,
        actionName("addToSubmitted")
      )
    },
    removeToSubmitted(id) {
      set(
        (state: { rmpm: { submitted: { [x: string]: string } } }) => {
          delete state.rmpm.submitted[id];
          return state
        },
        undefined,
        actionName("removeToSubmitted")
      )
    },
    addProductsToRmTruck(sheetId, schemaIds, productSheetIds) {
      set(
        (state: { rmpm: { trucks: { rm: any[] }; allProducts: { rmProducts: RMProductSchema[] } } }) => {
          const truck = state.rmpm.trucks.rm.find(
            (truck) => truck.sheetId === sheetId
          )
          if (!truck) {
            return
          }

          const newProductSchemas = mapRmProductIdsToSchemas(
            state.rmpm.allProducts.rmProducts,
            schemaIds
          )

          // Add productSheetIds to the schemas
          if (productSheetIds) {
            for (const schema of newProductSchemas) {
              if (schema.id && productSheetIds[schema.id]) {
                schema.productSheetId = productSheetIds[schema.id]
              }
            }
          }

          truck.properties?.products.items?.push(...newProductSchemas)

          return state
        },
        undefined,
        actionName("addProductsToRmTruck")
      )
    },
    removeRmProduct(sheetId, productId) {
      set(
        (state: { rmpm: { trucks: { rm: any[] }; forms: { rm: any[] } } }) => {
          const truck = state.rmpm.trucks.rm.find(
            (truck) => truck.sheetId === sheetId
          )
          if (!truck) {
            return
          }

          if (truck.properties?.products.items) {
            truck.properties.products.items =
              truck.properties.products.items?.filter(
                (item: { id: string }) => item.id !== productId
              )

            if (!truck.properties.products.items.length) {
              state.rmpm.trucks.rm = state.rmpm.trucks.rm.filter(
                (truck) => truck.sheetId !== sheetId
              )
            }
          }

          const truckForm = state.rmpm.forms.rm.find(truck => truck.sheetId === sheetId);

          if (truckForm?.products) {
            const filteredProducts = Object.entries(truckForm.products)
              .filter(([key]) => key !== "recordedAt" && key !== "value" && key !== "status" && key !== "required")
              .filter(([_, val]) => (val as { id: string })?.id !== productId);

            const newProducts = Object.fromEntries(filteredProducts);

            if (truckForm.products.recordedAt) newProducts.recordedAt = truckForm.products.recordedAt;
            if (truckForm.products.value) newProducts.value = truckForm.products.value;
            if (truckForm.products.status) newProducts.status = truckForm.products.status;
            if (truckForm.products.required !== undefined) newProducts.required = truckForm.products.required;

            truckForm.products = newProducts;

            const hasProducts = Object.keys(newProducts).some(
              k => !["recordedAt", "value", "status", "required"].includes(k)
            );

            if (!hasProducts) {
              state.rmpm.forms.rm = state.rmpm.forms.rm.filter(t => t.sheetId !== sheetId);
            }

          }
          return state
        },
        undefined,
        actionName("removeRmProduct")
      )
    },
    addProductsToPmTruck(sheetId, schemaIds, productSheetIds) {
      set(
        (state: { rmpm: { trucks: { pm: any[] }; allProducts: { pmProducts: PMProductSchema[] } } }) => {
          const truck = state.rmpm.trucks.pm.find(
            (truck) => truck.sheetId === sheetId
          )
          if (!truck) {
            return
          }

          const newProductSchemas = mapPmProductIdsToSchemas(
            state.rmpm.allProducts.pmProducts,
            schemaIds,
            truck.properties.products.items.length
          )

          // Add productSheetIds to the schemas
          if (productSheetIds) {
            for (const schema of newProductSchemas) {
              if (schema.id && productSheetIds[schema.id]) {
                schema.productSheetId = productSheetIds[schema.id]
              }
            }
          }

          truck.properties?.products.items?.push(...newProductSchemas)

          return state
        },
        undefined,
        actionName("addProductsToPmTruck")
      )
    },
    removePmProduct(sheetId, productId) {
      set(
        (state: { rmpm: { trucks: { pm: any[] }; forms: { pm: any[]; rm: any } } }) => {
          const truck = state.rmpm.trucks.pm.find(
            (truck) => truck.sheetId === sheetId
          )
          if (!truck) {
            return
          }

          if (truck.properties?.products.items) {
            truck.properties.products.items =
              truck.properties.products.items?.filter(
                (item: { id: string }) => item.id !== productId
              )

            if (!truck.properties.products.items.length) {
              state.rmpm.trucks.pm = state.rmpm.trucks.pm.filter(
                (truck) => truck.sheetId !== sheetId
              )
            }
          }

          const truckForm = state.rmpm.forms.pm.find(truck => truck.sheetId === sheetId);

          if (truckForm?.products) {
            const filteredProducts = Object.entries(truckForm.products)
              .filter(([key]) => key !== "recordedAt" && key !== "value" && key !== "status" && key !== "required")
              .filter(([_, val]) => (val as { id: string })?.id !== productId);

            const newProducts = Object.fromEntries(filteredProducts);

            if (truckForm.products.recordedAt) newProducts.recordedAt = truckForm.products.recordedAt;
            if (truckForm.products.value) newProducts.value = truckForm.products.value;
            if (truckForm.products.status) newProducts.status = truckForm.products.status;
            if (truckForm.products.required !== undefined) newProducts.required = truckForm.products.required;

            truckForm.products = newProducts;

            const hasProducts = Object.keys(newProducts).some(
              k => !["recordedAt", "value", "status", "required"].includes(k)
            );

            if (!hasProducts) {
              state.rmpm.forms.pm = state.rmpm.forms.pm.filter(t => t.sheetId !== sheetId);
            }

          }

          return state
        },
        undefined,
        actionName("removePmProduct")
      )
    },
    async addSkuToProduct(
      truckType: string, 
      sheetId: string, 
      addToPath: string, 
      schema: Schema,
      productInfo: { productId: string; productName: string; skuName: string; market?: "DOMESTIC" | "EXPORT" }
    ) {
      // Create a product sheet for this SKU on the server first
      let skuProductSheetId: string | undefined;
      try {
        const response = await createRMPMProductSheet(sheetId, {
          productId: productInfo.productId,
          productName: productInfo.productName,
          sku: productInfo.skuName,
          market: productInfo.market,
        });
        skuProductSheetId = response.productSheetId;
      } catch (error) {
        console.error('[addSkuToProduct] Failed to create product sheet for SKU:', error);
        throw new Error('Failed to create product sheet for SKU. Please try again.');
      }

      set(
        (state: { rmpm: { trucks: Record<string, any[]> } }) => {

          const truck = state.rmpm.trucks[truckType].find(t => t.sheetId === sheetId);
          if (!truck) return;

          const skus = lodashGet(truck, addToPath) as Schema | undefined
          if (!skus) return

          if (!skus.items) {
            skus.items = []
          }

          const newSchema = cloneDeep(schema);

          // Generate a unique instance ID for this SKU item
          // This ensures each SKU instance can be deleted individually
          const instanceId = createUuid();
          newSchema.id = instanceId;
          
          // Assign the server-generated productSheetId for this SKU
          newSchema.productSheetId = skuProductSheetId;

          newSchema.options = {
            ...newSchema.options,
            widget: ParamSchemaType.CARD_SECTION,
            hideLabel: true,
            removable: true
          };

          // Remove nested skus if exists
          delete newSchema.properties?.skus;

          newSchema.properties ??= {};

          newSchema.properties.id = {
            type: "string",
            default: instanceId,
            const: instanceId,
            required: true
          };

          const productId = instanceId;

          const parameters = newSchema.properties?.parameters?.items ?? [];

          // Set default Market value to "Domestic" for new SKU
          // Clone the parameters array to avoid shared references
          if (parameters.length > 0 && parameters[0]?.properties?.value) {
            // Create a deep clone of the market parameter to avoid shared state
            const marketParam = cloneDeep(parameters[0]);
            marketParam.properties!.value.default = "Domestic";
            parameters[0] = marketParam;
          }

          for (const item of parameters) {
            const conditions = item?.if?.allOf ?? [];

            for (const cond of conditions) {
              if (cond?.const === "Domestic" && item.then) {
                item.then.productId = productId;
              }

              if (cond?.const === "Export" && item.then) {
                item.then.productId = productId;
              }
            }
          }

          skus.items.push(setSkuDefaultEmpty(newSchema));

          return state;
        },
        undefined,
        actionName("addSkuToProduct")
      );
    },
    removeSkuFromProduct(truckType: TruckType, sheetId: string, skuPath: string, itemId: string) {
      set(
        (state) => {
          // Validate truckType exists
          if (!state.rmpm.trucks[truckType]) {
            console.error('[removeSkuFromProduct] Invalid truck type:', truckType)
            return
          }
          
          const truck = state.rmpm.trucks[truckType].find(t => t.sheetId === sheetId);
          if (!truck) {
            console.error('[removeSkuFromProduct] Truck not found:', sheetId)
            return
          }

          // Remove SKU from schema by unique itemId (mutations tracked by Immer)
          const skus = lodashGet(truck, skuPath) as Schema | undefined;
          if (skus?.items) {
            skus.items = skus.items.filter((item: Schema) => item.id !== itemId);
          }

          // Remove SKU from form data (mutations tracked by Immer)
          const truckForm = state.rmpm.forms[truckType].find((f) => f.sheetId === sheetId);
          if (truckForm?.products) {
            // The skuPath looks like: properties.products.items[0].properties.skus
            // We need to find the corresponding product in form data by index
            const pathParts = skuPath.split('.');
            const productIndexMatch = pathParts.find(p => p.includes('items['));
            if (productIndexMatch) {
              const productIndex = parseInt(productIndexMatch.match(/\d+/)?.[0] || '0');
              
              // Get product keys (excluding metadata keys) and find the one at the correct index
              const productKeys = Object.keys(truckForm.products).filter((k) => 
                !['recordedAt', 'value', 'status', 'required'].includes(k)
              );
              const productKey = productKeys[productIndex];
              
              if (productKey && truckForm.products[productKey]?.skus) {
                const skusData = truckForm.products[productKey].skus as Record<string, unknown>;
                
                // Filter out the SKU with matching unique itemId
                // Each SKU instance has a unique id generated by createUuid() in addSkuToProduct
                const filteredSkus = Object.fromEntries(
                  Object.entries(skusData).filter(([key, value]) => {
                    // Keep metadata keys
                    if (['recordedAt', 'value', 'status', 'required'].includes(key)) {
                      return true;
                    }
                    // Filter out SKU with matching unique itemId (not skuId)
                    const skuItem = value as { id?: string } | undefined;
                    return skuItem?.id !== itemId;
                  })
                );
                
                truckForm.products[productKey].skus = filteredSkus;
              }
            }
          }
          // Note: With Immer middleware, explicit return is not required - mutations are tracked automatically
        },
        undefined,
        actionName("removeSkuFromProduct")
      );
    },

  },
})

function setSkuDefaultEmpty(schema: any) {
  if (!schema) return;

  if (schema?.options?.label === "SKU" && schema?.properties?.value) {
    schema.properties.value.default = "";
  }

  if (schema.properties && typeof schema.properties === "object") {
    Object.values(schema.properties).forEach(child => setSkuDefaultEmpty(child));
  }

  if (Array.isArray(schema.items)) {
    schema.items.forEach((item: any) => setSkuDefaultEmpty(item));
  }

  return schema
}



export function selectRmForm(sheetId: string) {
  return (slice: RMPMSlice["rmpm"]) => {
    return slice.forms.rm.find((form) => form.sheetId === sheetId)
  }
}

export function selectPmForm(sheetId: string) {
  return (slice: RMPMSlice["rmpm"]) => {
    return slice.forms.pm.find((form) => form.sheetId === sheetId)
  }
}

export function selectRmTruck(sheetId: string) {
  return (slice: RMPMSlice["rmpm"]) => {
    return slice.trucks.rm.find((truck) => truck.sheetId === sheetId)
  }
}

export function selectPmTruck(sheetId: string) {
  return (slice: RMPMSlice["rmpm"]) => {
    return slice.trucks.pm.find((truck) => truck.sheetId === sheetId)
  }
}

function mapPmProductIdsToSchemas(
  allSchemas: PMProductSchema[],
  ids: string[],
  startIndex: number
): Schema[] {
  const idSet = new Set(ids)

  return allSchemas
    .filter((schema) => idSet.has(schema.id))
    .map((schema, index) => {
      let result: Schema
      result = buildSchemaForPmProduct(
        schema,
        `properties.products.items[${startIndex + index}]`
      )
      return result
    })
}

function mapRmProductIdsToSchemas(
  allSchemas: RMProductSchema[],
  ids: string[]
): Schema[] {
  const idSet = new Set(ids)

  return allSchemas
    .filter((schema) => idSet.has(schema.id))
    .map((schema) => {
      return buildSchemaForRmProduct(schema)
    })
}

export default createRMPMSlice
